import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch, httpClient } from "../../HttpClient";

const CLIENTS_URL = "https://backend-myalfa.vercel.app/api/clients";

export async function fetchClientList() {
  const { data } = await httpClient.get(`${CLIENTS_URL}?all=true`);
  return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
}

async function fetchClientDetail(id) {
  const { data } = await httpClient.get(`${CLIENTS_URL}/${id}`);
  return data;
}

// Détail du client par tranches (GET /clients/:id?scope=core|timeline|extras,
// voir getClientById côté backend) : la fiche s'affiche dès que "core"
// (identité, comptes, contacts Annuaire, équipe) arrive, sans attendre la
// timeline ni les relations lourdes.
export async function fetchClientScope(id, scope) {
  const { data } = await httpClient.get(`${CLIENTS_URL}/${id}?scope=${scope}`);
  return data;
}

// Partagé entre useClient et FicheClientOverlay (contacts Annuaire liés) :
// même clé de cache, donc une seule requête "core" en vol par fiche ouverte.
export const clientCoreQuery = (id) => ({
  queryKey: ["client", id, "core"],
  queryFn: () => fetchClientScope(id, "core"),
  staleTime: 0,
});

// Liste complète des clients. staleTime: Infinity car plusieurs écrans
// (recherche client dans SignModif notamment) dépendent d'un filtrage
// instantané en mémoire, sans aller-retour réseau à chaque frappe — la
// donnée est préchargée une fois au login (EtatGlobal.js) puis ne se
// rafraîchit qu'explicitement (invalidation après création/édition).
export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: fetchClientList,
    staleTime: Infinity,
  });
}

// Détail complet d'un client (toutes relations incluses), pour FicheClientOverlay.
export function useClient(id) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["client", id],
    queryFn: async () => {
      // Chaque tranche est publiée dans le cache dès qu'elle arrive (fusionnée
      // avec ce qui s'y trouve déjà, pour qu'une fiche déjà ouverte ne
      // « clignote » pas) — isLoading repasse à false après "core".
      const publish = (partial) =>
        queryClient.setQueryData(["client", id], (prev) => ({
          ...(prev || {}),
          ...partial,
        }));

      // "core" seul d'abord : lancé en même temps que les autres il serait
      // ralenti par la concurrence sur la connexion à la base.
      const core = await queryClient.fetchQuery(clientCoreQuery(id));
      publish(core);

      await Promise.all([
        fetchClientScope(id, "timeline").then(publish),
        fetchClientScope(id, "extras").then(publish),
      ]);
      // Le cache contient déjà chaque tranche (publish) ET toute modification
      // faite entre-temps (mise à jour optimiste, rechargement après
      // enregistrement) : on le renvoie tel quel. Réétaler `core` ici
      // remettait l'ancienne valeur d'un champ enregistré pendant le
      // chargement (ex. pays du siège qui « ne change pas » à l'écran).
      return queryClient.getQueryData(["client", id]);
    },
    enabled: !!id,
  });
}

// Met à jour un client. `payload` ne doit contenir que les champs scalaires
// modifiables (voir EDITABLE_CLIENT_FIELDS dans Update.js) — le corps du PUT
// ne doit pas embarquer les relations en lecture seule du client (historiques,
// todos, documents...), sous peine de dépasser la limite de taille de requête
// du serveur pour un dossier avec beaucoup d'historique.
export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const response = await authFetch(`${CLIENTS_URL}/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        let detail = "";
        try {
          const errBody = await response.json();
          detail = errBody?.message || JSON.stringify(errBody);
        } catch {}
        throw new Error(
          `Erreur serveur (${response.status})${detail ? " : " + detail : ""}`
        );
      }
      return response.json();
    },
    // Les champs envoyés sont appliqués tout de suite au cache partagé
    // (fiche, liste, Centre de contrôle conformité) : l'écran n'attend pas
    // le rechargement complet du dossier, qui peut être lent. Revert si le
    // serveur refuse.
    onMutate: ({ id, payload }) => {
      const avant = {
        client: queryClient.getQueryData(["client", id]),
        clients: queryClient.getQueryData(["clients"]),
      };
      queryClient.setQueryData(["client", id], (prev) =>
        prev ? { ...prev, ...payload } : prev
      );
      queryClient.setQueryData(["clients"], (prev) =>
        Array.isArray(prev)
          ? prev.map((c) => (c.id === id ? { ...c, ...payload } : c))
          : prev
      );
      return avant;
    },
    onError: (_err, { id }, avant) => {
      if (!avant) return;
      queryClient.setQueryData(["client", id], avant.client);
      queryClient.setQueryData(["clients"], avant.clients);
    },
    onSuccess: (_serverData, { id }) => {
      // Le PUT a déjà réussi à ce stade — recharger le client complet (toutes
      // relations, pour que le cache reflète exactement ce qu'un rechargement
      // de page afficherait) se fait en tâche de fond, SANS être attendu :
      // cette requête peut être lente sur un dossier avec beaucoup
      // d'historique, et ne doit jamais bloquer le retour de la mutation
      // (donc le toast et tout code appelant onSuccess/onError).
      fetchClientDetail(id)
        .then((fullClient) => {
          queryClient.setQueryData(["client", id], fullClient);
          queryClient.setQueryData(["clients"], (prev) =>
            Array.isArray(prev)
              ? prev.map((c) => (c.id === id ? fullClient : c))
              : prev
          );
        })
        .catch(() => {
          queryClient.invalidateQueries({ queryKey: ["clients"] });
          queryClient.invalidateQueries({ queryKey: ["client", id] });
        });
    },
  });
}
